import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  tap,
} from 'rxjs';
import { AuthService } from '@app/features/auth/core/auth.service';
import { NotificationService } from '@app/shared/notifications/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { SupportSessionApiService } from './support-session-api.service';
import {
  EndImpersonationResponse,
  ImpersonationContext,
  StartImpersonationResponse,
} from './support-session.model';

const RETURN_URL_KEY = 'support_session_admin_return_url';
const BODY_ACTIVE_CLASS = 'support-session-active';
const TITLE_PREFIX = 'SUPPORT: ';

/**
 * Public, unauthenticated routes where the support banner / body indicator /
 * title prefix must be hidden — even if the admin happens to view them while
 * an impersonation token is active. These pages can be shared with end users
 * and must not leak that an admin is in a support session.
 */
const PUBLIC_ROUTE_PATTERNS: RegExp[] = [
  /^\/websites\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:[\/?#]|$)/i,
];

@Injectable({
  providedIn: 'root',
})
export class ImpersonationService {
  private authService = inject(AuthService);
  private apiService = inject(SupportSessionApiService);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private translate = inject(TranslateService);

  private contextSubject = new BehaviorSubject<ImpersonationContext | null>(
    null,
  );
  readonly context$: Observable<ImpersonationContext | null> =
    this.contextSubject.asObservable();

  private isPublicRouteSubject = new BehaviorSubject<boolean>(false);

  /**
   * Emits the impersonation context only when the banner should be visible.
   * Returns null on public routes (e.g. shared website pages) so the banner,
   * body styling and tab title prefix don't leak to anonymous viewers.
   */
  readonly visibleContext$: Observable<ImpersonationContext | null> =
    combineLatest([this.contextSubject, this.isPublicRouteSubject]).pipe(
      map(([context, isPublic]) => (isPublic ? null : context)),
      distinctUntilChanged(),
    );

  constructor() {
    this.listenForRouteChanges();
    this.rehydrateFromToken();
    this.listenForCrossTabAuthChanges();
  }

  /**
   * On app boot, if the stored JWT is an impersonation token, rebuild the
   * in-memory context so the banner and all guards behave correctly after a
   * page refresh.
   */
  private rehydrateFromToken(): void {
    const claims = this.authService.getImpersonationClaims();
    const previousAdminToken = this.authService.getPreviousAdminToken();

    if (!claims) {
      this.contextSubject.next(null);
      this.refreshVisualIndicators();
      return;
    }

    this.contextSubject.next({
      sessionId: claims.sid,
      adminId: claims.actId,
      adminEmail: claims.actEmail,
      targetUserId: claims.targetUserId,
      targetUserEmail: claims.targetUserEmail,
      expiresAt: claims.expiresAt,
      previousAdminToken: previousAdminToken ?? '',
    });
    this.refreshVisualIndicators();
  }

  /**
   * Tracks whether the current URL is a public, shareable page so the banner
   * and body/title indicators can be suppressed there.
   *
   * The initial value is read synchronously from `window.location` because at
   * service construction time the Angular router has not finished its first
   * navigation yet (`router.url` is still `'/'`). Using `location` avoids a
   * one-frame flash of the banner when the user refreshes the page on a
   * public route.
   */
  private listenForRouteChanges(): void {
    this.isPublicRouteSubject.next(this.isPublicUrl(this.readCurrentUrl()));

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        map((event) => event.urlAfterRedirects || event.url),
        map((url) => this.isPublicUrl(url)),
        distinctUntilChanged(),
      )
      .subscribe((isPublic) => {
        this.isPublicRouteSubject.next(isPublic);
        this.refreshVisualIndicators();
      });
  }

  private readCurrentUrl(): string {
    if (typeof window === 'undefined') {
      return this.router.url;
    }
    return (
      window.location.pathname + window.location.search + window.location.hash
    );
  }

  private isPublicUrl(url: string): boolean {
    if (!url) {
      return false;
    }
    return PUBLIC_ROUTE_PATTERNS.some((pattern) => pattern.test(url));
  }

  /**
   * When the support session is ended (or started) from another tab, the
   * `storage` event fires here so we can sync this tab's in-memory state with
   * the new token. Without this, ending a session in tab A would leave tab B
   * with a stale impersonation context and continued access to the user's
   * account.
   */
  private listenForCrossTabAuthChanges(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('storage', (event: StorageEvent) => {
      const isAuthTokenChange =
        event.key === this.authService.TOKEN_KEY ||
        event.key === this.authService.PREVIOUS_ADMIN_TOKEN_KEY ||
        event.key === null;

      if (!isAuthTokenChange) {
        return;
      }

      this.handleExternalAuthChange();
    });
  }

  private handleExternalAuthChange(): void {
    this.authService.reloadDecodedToken();

    const previousContext = this.contextSubject.value;
    const wasImpersonating = previousContext !== null;
    const isNowImpersonating = this.authService.isImpersonation();

    if (wasImpersonating && !isNowImpersonating) {
      this.authService.clearPreviousAdminToken();
      this.contextSubject.next(null);
      this.refreshVisualIndicators();

      this.notificationService.info(
        this.translate.instant('supportSession.toasts.endedFromAnotherTab'),
      );

      const returnUrl =
        sessionStorage.getItem(RETURN_URL_KEY) || '/admin/users';
      sessionStorage.removeItem(RETURN_URL_KEY);
      this.router.navigateByUrl(returnUrl).then();
      return;
    }

    if (!wasImpersonating && isNowImpersonating) {
      this.rehydrateFromToken();
      return;
    }

    if (wasImpersonating && isNowImpersonating) {
      const claims = this.authService.getImpersonationClaims();
      if (claims && claims.sid !== previousContext.sessionId) {
        this.rehydrateFromToken();
      }
    }
  }

  isImpersonating(): boolean {
    return this.contextSubject.value !== null;
  }

  getContext(): ImpersonationContext | null {
    return this.contextSubject.value;
  }

  startImpersonation(
    targetUserId: string,
    reason: string,
    silent = false,
  ): Observable<StartImpersonationResponse> {
    const previousAdminToken = this.authService.getToken();
    const returnUrl = this.router.url || '/admin/users';
    sessionStorage.setItem(RETURN_URL_KEY, returnUrl);

    const request = { targetUserId, reason, silent };
    return this.apiService.startImpersonation(request).pipe(
      tap((response) => {
        if (previousAdminToken) {
          this.authService.savePreviousAdminToken(previousAdminToken);
        }
        this.authService.saveToken(response.token);

        this.contextSubject.next({
          sessionId: response.session.id,
          adminId: response.session.adminId,
          adminEmail: response.session.adminEmail,
          targetUserId: response.session.targetUserId,
          targetUserEmail: response.session.targetUserEmail,
          expiresAt: new Date(response.session.expiresAt),
          previousAdminToken: previousAdminToken ?? '',
        });

        this.refreshVisualIndicators();

        this.notificationService.success(
          this.translate.instant('supportSession.toasts.started', {
            email: response.session.targetUserEmail,
          }),
        );

        this.router.navigateByUrl('/').then();
      }),
    );
  }

  endImpersonation(): Observable<EndImpersonationResponse> {
    return this.apiService.endImpersonation().pipe(
      tap((response) => {
        this.authService.saveToken(response.adminToken);
        this.finalizeEnd();

        this.notificationService.success(
          this.translate.instant('supportSession.toasts.ended'),
        );
      }),
    );
  }

  /**
   * Defensive fallback: if the backend /end call fails or the token already
   * expired (401), drop the impersonation token and restore the stored admin
   * token without any server round trip.
   */
  restorePreviousAdminTokenLocally(): boolean {
    const previousAdminToken = this.authService.getPreviousAdminToken();
    if (!previousAdminToken) {
      this.finalizeEnd();
      return false;
    }

    this.authService.saveToken(previousAdminToken);
    this.finalizeEnd();
    return true;
  }

  private finalizeEnd(): void {
    this.authService.clearPreviousAdminToken();
    this.contextSubject.next(null);
    this.refreshVisualIndicators();

    const returnUrl = sessionStorage.getItem(RETURN_URL_KEY) || '/admin/users';
    sessionStorage.removeItem(RETURN_URL_KEY);
    this.router.navigateByUrl(returnUrl).then();
  }

  private refreshVisualIndicators(): void {
    const active =
      this.contextSubject.value !== null && !this.isPublicRouteSubject.value;
    this.applyVisualIndicators(active);
  }

  private applyVisualIndicators(active: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (active) {
      document.body.classList.add(BODY_ACTIVE_CLASS);
      if (!document.title.startsWith(TITLE_PREFIX)) {
        document.title = TITLE_PREFIX + document.title;
      }
    } else {
      document.body.classList.remove(BODY_ACTIVE_CLASS);
      if (document.title.startsWith(TITLE_PREFIX)) {
        document.title = document.title.substring(TITLE_PREFIX.length);
      }
    }
  }
}
